// Catálogo del portal del vendedor — explorer de tres columnas del mock `portal-vendedor-catalogo.html`.
// Una sola lectura paginada (`GET /portal/catalog`); filtros, orden y conteos van sobre lo cargado.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ApiError } from '@/api/errors';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import {
    loadedFiltersNote,
    PRODUCTS_NOUN,
    LOADED_PAGE_SIZE,
    useLoadedPaginationTable,
} from '@/shared/patterns/LoadedScope';
import { useStoredFlag } from '@/shared/hooks/useStoredFlag';
import { useSellerContext } from '@/domains/sales/wizard/sellerContext';
import { useRecentSales, useStoreSales } from './api/usePortal';
import { usePortalCatalog, type PortalCatalogResult } from './catalog/api/usePortalCatalog';
import { CatalogFiltersPanel } from './catalog/components/CatalogFiltersPanel';
import { CatalogTable } from './catalog/components/CatalogTable';
import {
    ProductDetailPanel,
    type LevelsState,
    type PendingLink,
    type ProductDetailTab,
    type SalesState,
} from './catalog/components/ProductDetailPanel';
import {
    brandOptions,
    catalogMetrics,
    countActiveFilters,
    DEFAULT_SORT,
    FILTERS_COLLAPSED_KEY,
    financierOptions,
    sortCatalog,
    type SortState,
    type StatFilter,
} from './catalog/lib/catalogExplorer';
import {
    catalogEntries,
    entryKeyOfSku,
    modelsAndVariantsLabel,
    type CatalogEntry,
} from './catalog/lib/modelGroups';
import type { PortalCatalogRow } from '@/api/services/retail-wizard/contract';
import type { Table } from '@tanstack/react-table';
import {
    catalogListErrorCopy,
    catalogNextPageReason,
    deepLinkStateOf,
    shouldAutoLoad,
    type DeepLinkState,
} from './catalog/lib/catalogList';
import {
    EMPTY_CATALOG_FILTERS,
    filterCatalogByLevel,
    levelPlansOf,
    type CatalogFilters,
} from './catalog/lib/catalogLevels';

export function SalesCatalog({ sku }: { sku?: string } = {}) {
    const navigate = useNavigate();
    const { storeId } = useSellerContext();
    const [filters, setFilters] = useState<CatalogFilters>(EMPTY_CATALOG_FILTERS);
    const [stat, setStat] = useState<StatFilter>('all');
    const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
    const [selectedSku, setSelectedSku] = useState<string | null>(sku ?? null);
    const [linkedSku, setLinkedSku] = useState(sku);
    const [tab, setTab] = useState<ProductDetailTab>('producto');
    const [filtersCollapsed, setFiltersCollapsed] = useStoredFlag(FILTERS_COLLAPSED_KEY);
    const searchRef = useRef<HTMLInputElement>(null);

    const list = usePortalCatalog();
    const rows = list.rows;
    const levelPlans = useMemo(() => levelPlansOf(list.firstPage), [list.firstPage]);

    // El deep-link `?sku=` manda sobre lo elegido a mano: llegar por una URL nueva mueve la ficha.
    if (sku !== linkedSku) {
        setLinkedSku(sku);
        setSelectedSku(sku ?? null);
    }

    const visibleRows = useMemo(
        () =>
            sortCatalog(filterCatalogByLevel(rows, filters, stat, levelPlans.plans), sort, {
                soldOutLast: false,
            }),
        [rows, filters, stat, levelPlans.plans, sort],
    );
    // Los teléfonos van una fila por modelo; lo demás, por SKU.
    const entries = useMemo(() => catalogEntries(visibleRows), [visibleRows]);
    // Las páginas llegan solas: la tabla no se regresa a la 1 con cada una, solo al cambiar filtros.
    const table = useLoadedPaginationTable(entries, LOADED_PAGE_SIZE, {
        autoResetPageIndex: false,
    });
    useResetPageOn(table, [filters, stat, sort]);
    const tablePageRows = table.getRowModel().rows;
    const pageEntries = useMemo(() => tablePageRows.map((row) => row.original), [tablePageRows]);
    const modelsCount = useMemo(() => {
        const groups = entries.flatMap((entry) => (entry.kind === 'model' ? [entry.group] : []));
        return groups.length === 0 ? null : modelsAndVariantsLabel(groups);
    }, [entries]);
    useRevealSkuPage(table, entries, selectedSku, isAutoLoading(list));
    useAutoLoadCatalog(list);
    const metrics = useMemo(() => catalogMetrics(visibleRows), [visibleRows]);
    const brands = useMemo(() => brandOptions(rows), [rows]);
    const financiers = useMemo(() => financierOptions(rows), [rows]);

    const pendingLink = usePendingLink(
        list,
        linkedSku !== undefined && selectedSku === linkedSku ? linkedSku : undefined,
    );

    /** Como el mock, la ficha queda vacía hasta elegir una fila; si el filtro se la lleva, se cierra. */
    const openedItem = useMemo(
        () => visibleRows.find((item) => item.sku === selectedSku) ?? null,
        [visibleRows, selectedSku],
    );
    const sales = useCatalogSales(tab === 'ventas' && openedItem !== null);
    const levels: LevelsState = {
        storeId,
        plans: levelPlans.plans,
        reason: levelPlans.note,
        isLoading: list.isPending,
    };

    // Atajo `/` para saltar al buscador sin soltar el teclado; no lo toma si ya se escribe en un campo.
    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== '/' || e.ctrlKey || e.metaKey) return;
            const active = document.activeElement;
            if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
            e.preventDefault();
            searchRef.current?.focus();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
        };
    }, []);

    /** Elegir a mano mueve también el `?sku=` de la URL, para que recargar abra la misma ficha. */
    const selectRow = (nextSku: string) => {
        setSelectedSku(nextSku);
        void navigate({
            to: '/sales/$section',
            params: { section: 'catalog' },
            search: { sku: nextSku },
            replace: true,
        });
    };

    /** «Iniciar venta» lleva el SKU a «Elige el teléfono», que abre con ese equipo elegido. */
    const startSale = (nextSku: string) => {
        void navigate({ to: '/wizard', search: { sku: nextSku } });
    };

    const scope = { loaded: rows.length, hasMore: list.hasNextPage };

    return (
        <ScreenFrame>
            <MasterDetailExplorer
                className="m-2 rounded-[10px] border border-border bg-card"
                filtersCollapsed={filtersCollapsed}
                filters={
                    <CatalogFiltersPanel
                        filters={filters}
                        brands={brands}
                        financiers={financiers}
                        levelsNote={levelPlans.note}
                        levelsUnavailable={
                            levelPlans.plans === null || levelPlans.plans.length === 0
                        }
                        loadedNote={loadedFiltersNote(scope, PRODUCTS_NOUN)}
                        onChange={setFilters}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed(!filtersCollapsed);
                        }}
                        searchRef={searchRef}
                    />
                }
                list={
                    <CatalogTable
                        table={table}
                        pageEntries={pageEntries}
                        allRows={rows}
                        filteredCount={visibleRows.length}
                        modelsCount={modelsCount}
                        metrics={metrics}
                        hasActiveFilters={countActiveFilters(filters) > 0 || filters.level !== ''}
                        stat={stat}
                        onStatChange={setStat}
                        sort={sort}
                        onSortChange={setSort}
                        selectedSku={openedItem?.sku ?? null}
                        onSelect={selectRow}
                        isLoading={list.isPending}
                        errorCopy={list.error ? catalogListErrorCopy(list.error) : null}
                        onRetry={list.retry}
                        hasNextPage={list.hasNextPage}
                        isFetchingNextPage={list.isFetchingNextPage}
                        onLoadMore={list.mustRestart ? list.restart : list.fetchNextPage}
                        nextPageError={
                            list.nextPageError === null
                                ? null
                                : catalogNextPageReason(list.nextPageError)
                        }
                    />
                }
                detail={
                    <div className="flex min-h-0 flex-1 flex-col bg-card">
                        <ProductDetailPanel
                            item={openedItem}
                            catalog={rows}
                            sales={sales}
                            levels={levels}
                            scopeWidePromotions={list.firstPage?.scopeWidePromotions}
                            tab={tab}
                            onTabChange={setTab}
                            onSell={startSale}
                            onSelectSku={selectRow}
                            pendingLink={pendingLink}
                            loadedCount={rows.length}
                            catalogComplete={!list.hasNextPage}
                        />
                    </div>
                }
            />
        </ScreenFrame>
    );
}

const PENDING_LINK_STATES: readonly DeepLinkState[] = ['searching', 'beyondLoaded', 'notFound'];

/** Estado del `?sku=` sobre lo cargado; al tope o al fallar una página se le pide al vendedor que lo busque. */
function usePendingLink(list: PortalCatalogResult, sku: string | undefined): PendingLink | null {
    const loadedSkus = useMemo(() => list.rows.map((row) => row.sku), [list.rows]);
    const state = deepLinkStateOf({
        sku: list.error ? undefined : sku,
        loadedSkus,
        isPending: list.isPending,
        hasNextPage: list.hasNextPage,
        pagesLoaded: list.pagesLoaded,
        nextPageFailed: list.nextPageError !== null,
    });
    return PENDING_LINK_STATES.includes(state) ? (state as PendingLink) : null;
}

/** Ventas de la tienda solo con la pestaña «Ventas hoy» abierta; con 403, las del vendedor. */
function useCatalogSales(enabled: boolean): SalesState {
    const store = useStoreSales({ enabled });
    const forbidden = store.error instanceof ApiError && store.error.httpStatus === 403;
    const own = useRecentSales({ enabled: enabled && forbidden });
    const source = forbidden ? own : store;
    return {
        rows: source.rows,
        isLoading: source.isLoading,
        error: source.error,
        scope: forbidden ? 'own' : 'store',
    };
}

/** Un SKU elegido desde fuera (`?sku=`, «Similares») lleva la tabla a la página de su fila, una vez. */
function useRevealSkuPage(
    table: Table<CatalogEntry<PortalCatalogRow>>,
    entries: readonly CatalogEntry<PortalCatalogRow>[],
    sku: string | null,
    loading: boolean,
): void {
    const revealedRef = useRef<string | null>(null);
    const { pageIndex, pageSize } = table.getState().pagination;
    useEffect(() => {
        if (sku === null || revealedRef.current === sku) return;
        const key = entryKeyOfSku(entries, sku);
        if (key === null) return;
        const page = Math.floor(entries.findIndex((entry) => entry.key === key) / pageSize);
        if (page !== pageIndex) table.setPageIndex(page);
        // Mientras lleguen páginas su fila puede moverse; se da por revelado cuando termina.
        else if (!loading) revealedRef.current = sku;
    }, [table, entries, sku, pageIndex, pageSize, loading]);
}

/** Regresa la tabla a la página 1 cuando cambia algo de la lista (no en el primer render). */
function useResetPageOn(
    table: Table<CatalogEntry<PortalCatalogRow>>,
    deps: readonly unknown[],
): void {
    const firstRef = useRef(true);
    useEffect(() => {
        if (firstRef.current) {
            firstRef.current = false;
            return;
        }
        table.setPageIndex(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reinicia solo cuando cambian los filtros
    }, deps);
}

/** Siguen llegando páginas solas: las mismas condiciones con que `useAutoLoadCatalog` pide otra. */
function isAutoLoading(list: PortalCatalogResult): boolean {
    return (
        list.isPending ||
        list.isFetchingNextPage ||
        shouldAutoLoad({
            hasNextPage: list.hasNextPage,
            isFetching: false,
            nextPageFailed: list.nextPageError !== null,
            pagesLoaded: list.pagesLoaded,
        })
    );
}

/** Trae solas las páginas que faltan, con tope: un modelo con variantes en otra página se partiría. */
function useAutoLoadCatalog(list: PortalCatalogResult): void {
    const {
        hasNextPage,
        isFetchingNextPage,
        isPending,
        nextPageError,
        pagesLoaded,
        fetchNextPage,
    } = list;
    useEffect(() => {
        if (
            shouldAutoLoad({
                hasNextPage,
                isFetching: isPending || isFetchingNextPage,
                nextPageFailed: nextPageError !== null,
                pagesLoaded,
            })
        ) {
            fetchNextPage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchNextPage se re-crea en cada render; depender de él dispararía el efecto en bucle
    }, [hasNextPage, isFetchingNextPage, isPending, nextPageError, pagesLoaded]);
}
